import { useEffect, useState, useCallback } from 'react';

// Declare the build timestamp injected by Vite at build time
declare const __BUILD_TIMESTAMP__: string;

// App version for display purposes - update manually with each major release
export const APP_VERSION = '1.0.2';

// Build ID that automatically changes with each publish
export const LOCAL_BUILD_ID = typeof __BUILD_TIMESTAMP__ !== 'undefined' ? __BUILD_TIMESTAMP__ : Date.now().toString();

// Release notes history - newest first
export const RELEASE_NOTES_HISTORY = [
  {
    version: '1.0.2',
    date: '2026-02-02',
    title: '🔧 Verbesserungen & Bugfixes',
    changes: [
      '⏰ Follow-Up-Planung respektiert jetzt deine Arbeitszeiten',
      '🔄 Race-Condition behoben: Units erhalten jetzt unterschiedliche Planungszeiten',
      '📱 Ordner-Menü optimiert für Mobile (öffnet direkt im Hauptmenü)',
      '🔔 Benachrichtigungen sind jetzt klickbar und navigieren zum Todo/E-Mail',
      '✅ Benachrichtigungen verschwinden nach dem Anklicken'
    ]
  },
  {
    version: '1.0.1',
    date: '2026-02-02',
    title: '🚀 Neue Features & Verbesserungen',
    changes: [
      '✉️ E-Mail-Count zeigt jetzt die echte Anzahl vom Mailserver',
      '🔔 Benachrichtigungen zeigen jetzt auch ungelesene E-Mails und neue Wissensbank-Artikel',
      '🔧 Verbesserte IMAP-Verbindung mit STARTTLS-Support',
      '📱 Automatische App-Updates mit Release Notes',
      '📋 Changelog im Menü hinzugefügt'
    ]
  },
  {
    version: '1.0.0',
    date: '2026-01-15',
    title: '🎉 D2D+ Launch',
    changes: [
      '🏘️ D2D-Modul: Gebiete, Straßen, Häuser und Wohneinheiten verwalten',
      '🏷️ Intelligentes Tag-System: BK, NA, HOT, KT, KI, WIN, NO',
      '📷 OCR-Scanner: Klingelschilder scannen und Einheiten automatisch anlegen',
      '🔄 Automatische NA-Markierung nach 1 Stunde ohne Aktion',
      '📅 Intelligente Follow-Up-Planung mit KI-basierter Terminierung',
      '👥 Gebiete mit Teammitgliedern teilen',
      '📁 Gebiete in Ordnern organisieren',
      '👨‍👩‍👧‍👦 Team-Management mit Einladungssystem',
      '🏆 Performance-Metriken und Punktesystem',
      '📊 Team-Statistiken mit Aktivitäts-Übersicht',
      '🎯 Benutzerdefinierte Leistungskennzahlen',
      '👤 Kundenverwaltung mit Verifizierungs-Flow',
      '📋 Bedarfsanalyse pro Kunde',
      '📁 Kundenakte mit Dokumentenverwaltung',
      '✅ Todo-System mit Fälligkeitsdaten',
      '✉️ E-Mail-Client mit IMAP/SMTP-Integration',
      '🤖 KI-gestützte E-Mail-Komposition',
      '📝 E-Mail-Signaturen und Vorlagen',
      '🏖️ Autoresponder mit KI- und Urlaubs-Modus',
      '📎 Anhänge in Kundenakte speichern',
      '📚 Team-Wissensbank mit Rich-Text-Editor',
      '📌 Artikel anheften und kategorisieren',
      '🤖 App-Assistent: KI-Chatbot für Hilfe',
      '🔔 Benachrichtigungssystem für Todos',
      '📱 Mobile-First Design',
      '🔄 Echtzeit-Synchronisation im Team',
      '📴 Offline-Unterstützung',
      '🎨 Organisations-Branding (Logo & Farbe)',
      '⚙️ Benutzerdefinierte Profilfelder pro Team',
      '🕐 Arbeitszeiten-Konfiguration'
    ]
  }
];

// Current release notes (latest version)
export const RELEASE_NOTES = RELEASE_NOTES_HISTORY[0];

interface VersionState {
  needsUpdate: boolean;
  showReleaseNotes: boolean;
  previousVersion: string | null;
}

// Check interval: 30 seconds normally, 15 seconds in standalone mode (PWA)
const CHECK_INTERVAL_MS = 30 * 1000;
const CHECK_INTERVAL_PWA_MS = 15 * 1000;

const getCheckInterval = (): number => {
  const isStandalone = 
    (window.navigator as any).standalone === true ||
    window.matchMedia('(display-mode: standalone)').matches;
  return isStandalone ? CHECK_INTERVAL_PWA_MS : CHECK_INTERVAL_MS;
};

const clearAllCaches = async () => {
  try {
    // Clear Service Worker caches
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      console.log(`🗑️ Lösche ${cacheNames.length} Cache(s)...`);
      await Promise.all(
        cacheNames.map(cacheName => caches.delete(cacheName))
      );
    }

    // Unregister service workers
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const registration of registrations) {
        await registration.unregister();
        console.log('🔄 Service Worker deregistriert');
      }
    }

    // Clear IndexedDB (offline cache)
    try {
      const databases = await indexedDB.databases?.() || [];
      for (const db of databases) {
        if (db.name) {
          indexedDB.deleteDatabase(db.name);
          console.log(`🗑️ IndexedDB "${db.name}" gelöscht`);
        }
      }
    } catch (idbError) {
      // indexedDB.databases() not supported in some browsers (e.g., older iOS Safari)
      console.log('⚠️ IndexedDB-Auflistung nicht unterstützt, überspringe...');
    }

    // Clear sessionStorage (but not localStorage - we need version info)
    sessionStorage.clear();
    console.log('🗑️ SessionStorage geleert');

    console.log('✅ Alle Caches geleert');
  } catch (error) {
    console.error('Fehler beim Cache-Löschen:', error);
  }
};

// Check if running in iOS standalone mode (PWA)
const isIOSStandalone = () => {
  return (
    (window.navigator as any).standalone === true ||
    window.matchMedia('(display-mode: standalone)').matches
  );
};

export function useVersionCheck() {
  const [versionState, setVersionState] = useState<VersionState>({
    needsUpdate: false,
    showReleaseNotes: false,
    previousVersion: null
  });

  // Check for remote updates by fetching version.json with cache-busting
  const checkForRemoteUpdate = useCallback(async () => {
    try {
      // Fetch with cache-busting to bypass browser cache
      const response = await fetch(`/version.json?t=${Date.now()}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });
      
      if (!response.ok) {
        console.log('⚠️ version.json nicht erreichbar');
        return;
      }
      
      const remoteData = await response.json();
      const remoteBuildId = remoteData.buildId;
      const storedBuildId = localStorage.getItem('app_build_id');
      
      console.log(`🔍 Remote-Check: server=${remoteBuildId}, local=${storedBuildId}`);
      
      // If remote build differs from stored, we need to update
      if (remoteBuildId && storedBuildId && remoteBuildId !== storedBuildId && remoteBuildId !== 'PLACEHOLDER_UPDATED_AT_BUILD') {
        console.log(`🔄 Neue Version auf Server erkannt! ${storedBuildId} → ${remoteBuildId}`);
        
        // Clear caches
        await clearAllCaches();
        
        // Update stored build ID
        localStorage.setItem('app_build_id', remoteBuildId);
        
        // Hard reload to get new JS bundle
        window.location.reload();
      }
    } catch (error) {
      console.error('Fehler beim Remote-Version-Check:', error);
    }
  }, []);

  useEffect(() => {
    // Initial local check
    checkLocalVersion();
    
    // Check remote version immediately
    checkForRemoteUpdate();
    
    // Set up interval for remote checks (more frequent in PWA standalone mode)
    const intervalId = setInterval(checkForRemoteUpdate, getCheckInterval());
    
    return () => clearInterval(intervalId);
  }, [checkForRemoteUpdate]);

  const checkLocalVersion = () => {
    const storedBuildId = localStorage.getItem('app_build_id');
    const storedVersion = localStorage.getItem('app_version');
    const lastSeenReleaseNotes = localStorage.getItem('last_seen_release_notes');
    
    console.log(`🔍 Local Build-Check: stored=${storedBuildId}, current=${LOCAL_BUILD_ID}`);
    
    if (!storedBuildId) {
      // First time user - store the build ID and version
      localStorage.setItem('app_build_id', LOCAL_BUILD_ID);
      localStorage.setItem('app_version', APP_VERSION);
      localStorage.setItem('last_seen_release_notes', APP_VERSION);
      return;
    }

    // Check if local build differs (happens after reload with new JS)
    if (storedBuildId !== LOCAL_BUILD_ID) {
      console.log(`🔄 Lokaler Build-Unterschied: ${storedBuildId} → ${LOCAL_BUILD_ID}`);
      
      // Update stored build ID
      localStorage.setItem('app_build_id', LOCAL_BUILD_ID);
      localStorage.setItem('app_version', APP_VERSION);
      
      // Show release notes if version changed
      if (storedVersion !== APP_VERSION && lastSeenReleaseNotes !== APP_VERSION) {
        setVersionState({
          needsUpdate: false,
          showReleaseNotes: true,
          previousVersion: storedVersion
        });
      }
      
      console.log('✅ App auf neuesten Build aktualisiert');
    } else if (lastSeenReleaseNotes !== APP_VERSION) {
      // Same build but hasn't seen release notes for new version yet
      setVersionState({
        needsUpdate: false,
        showReleaseNotes: true,
        previousVersion: storedVersion
      });
    }
  };

  const dismissReleaseNotes = () => {
    localStorage.setItem('last_seen_release_notes', APP_VERSION);
    setVersionState(prev => ({ ...prev, showReleaseNotes: false }));
  };

  return {
    ...versionState,
    currentVersion: APP_VERSION,
    releaseNotes: RELEASE_NOTES,
    dismissReleaseNotes
  };
}
